import {
	createRouter,
	createWebHistory,
	NavigationGuardNext,
	RouteLocationNormalizedGeneric,
	RouteLocationNormalizedLoadedGeneric,
} from 'vue-router';
import { useAuthStore } from '@/stores/auth.ts';
import { useOnboardingStore } from '@/stores/onboarding.ts';
import { client } from 'laravel-precognition-vue';
import Dashboard from '@/pages/User/Dashboard.vue';
import { useStorage } from '@vueuse/core';
import { useUserStore } from '@/stores/user.ts';
import Access from '@/pages/User/Access.vue';
import MfaValidation from '@/pages/Mfa/MfaValidation.vue';
import ForceMfa from '@/pages/Mfa/ForceMfa.vue';
import Maintenance from '@/pages/Maintenance.vue';
import { fragmentParser } from '@/utils/fragmentParser.ts';
import MfaTotpRecoveryValidation from '@/pages/Mfa/MfaTotpRecoveryValidation.vue';
import { nextTick } from 'vue';
import { AnalyticsEvents, pushEvent } from '@/utils/analytics.ts';
import PartnerLinkSuccess from '@/pages/Login/PartnerLinkSuccess.vue';
import LoggedOutLayout from '@/layouts/LoggedOutLayout.vue';
import MfaLayout from '@/layouts/MfaLayout.vue';
import LoggedInLayout from '@/layouts/LoggedInLayout.vue';
import AccountLocked from '@/pages/AccountLocked.vue';

const Logout = () => import('@/pages/Login/Logout.vue');
const Login = () => import('@/pages/Login/Login.vue');
const LoginEmail = () => import('@/pages/Login/LoginEmail.vue');
const LoginPhone = () => import('@/pages/Login/LoginPhone.vue');
const Onboarding = () => import('@/pages/Onboarding/Onboarding.vue');
const OnBoardingHome = () => import('@/pages/Onboarding/OnboardingHome.vue');
const OnboardingCard = () => import('@/pages/Onboarding/OnboardingCard.vue');
const OnboardingCode = () => import('@/pages/Onboarding/OnboardingCode.vue');
const OnboardingTerms = () => import('@/pages/Onboarding/OnboardingTerms.vue');
const OnboardingCreate = () => import('@/pages/Onboarding/OnboardingCreate.vue');
const OnboardingMfaPhoneEntry = () => import('@/pages/Onboarding/OnboardingMfaPhoneEntry.vue');
const OnboardingTotpVerification = () => import('@/pages/Onboarding/OnboardingTotpVerification.vue');
const OnboardingTotpRecoveryKeys = () => import('@/pages/Onboarding/OnboardingTotpRecoveryKeys.vue');
const OnboardingMagicLink = () => import('@/pages/Onboarding/OnboardingMagicLink.vue');
const ItsmeAccountRecovery = () => import('@/pages/ItsmeAccountRecovery.vue');
const OnboardingMfaMethodChoice = () => import('@/pages/Onboarding/OnboardingMfaMethodChoice.vue');
const OnboardingPasswordSetup = () => import('@/pages/Onboarding/OnboardingPasswordSetup.vue');
const OnboardingConfirmation = () => import('@/pages/Onboarding/OnboardingConfirmation.vue');
const OnboardingMFA = () => import('@/pages/Onboarding/OnboardingMFA.vue');
const Approve = () => import('@/pages/Approve.vue');
const Partner = () => import('@/pages/Partner.vue');
const NeedHelp = () => import('@/pages/NeedHelp.vue');
const ForgotPassword = () => import('@/pages/ForgotPassword/ForgotPassword.vue');
const ForgotPasswordEmail = () => import('@/pages/ForgotPassword/ForgotPasswordEmail.vue');
const ForgotPasswordPhone = () => import('@/pages/ForgotPassword/ForgotPasswordPhone.vue');
const ForgotPasswordSent = () => import('@/pages/ForgotPasswordSent.vue');
const ResetPassword = () => import('@/pages/ResetPassword.vue');
const ResetPasswordConfirmed = () => import('@/pages/ResetPasswordConfirmed.vue');
const WeakPassword = () => import('@/pages/WeakPassword.vue');
const ForgotUsernameResponse = () => import('@/pages/ForgotUsername/ForgotUsernameResponse.vue');
const OnboardingAccountFound = () => import('@/pages/Onboarding/OnboardingAccountFound.vue');
const OnboardingAccountMerged = () => import('@/pages/Onboarding/OnboardingAccountMerged.vue');
const SetupMfaSelectionCustomer = () => import('@/pages/Mfa/SetupMfaSelectionCustomer.vue');
const SetupMfaPhone = () => import('@/pages/Mfa/SetupMfaPhone.vue');
const SetupMfaEmail = () => import('@/pages/Mfa/SetupMfaEmail.vue');
const SetupMfaTotp = () => import('@/pages/Mfa/SetupMfaTotp.vue');
const SetupMfaConfirmation = () => import('@/pages/Mfa/SetupMfaConfirmation.vue');
const SetupMfaSuccess = () => import('@/pages/Mfa/SetupMfaSuccess.vue');
const SetupTotpSuccess = () => import('@/pages/Mfa/SetupTotpSuccess.vue');
const SetupTotpSuccessRedirect = () => import('@/pages/Mfa/SetupTotpSuccessRedirect.vue');
const MfaMethodSelection = () => import('@/pages/Mfa/MfaMethodSelection.vue');
const MfaTooManyAttempts = () => import('@/pages/Mfa/MfaTooManyAttempts.vue');
const ProfileOverview = () => import('@/pages/Profile/Overview.vue');
const EditInfo = () => import('@/pages/Profile/EditInfo.vue');
const EditCredentials = () => import('@/pages/Profile/EditCredentials.vue');
const EditRecovery = () => import('@/pages/Security/EditRecovery.vue');
const SecurityOverview = () => import('@/pages/Security/Overview.vue');
const PasswordEdit = () => import('@/pages/Security/PasswordEdit.vue');
const History = () => import('@/pages/Security/History.vue');
const MfaEdit = () => import('@/pages/Security/MfaEdit.vue');
const MicrosoftCallback = () => import('@/pages/Login/MicrosoftCallback.vue');
const PartnerRequestAccess = () => import('@/pages/Login/PartnerRequestAccess.vue');
const PartnerNoAccount = () => import('@/pages/Login/PartnerNoAccount.vue');
const PartnerLinkExistingAccount = () => import('@/pages/Login/PartnerLinkExistingAccount.vue');
const ForgotUsername = () => import('@/pages/ForgotUsername/ForgotUsername.vue');
const ForgotUsernameNISS = () => import('@/pages/ForgotUsername/ForgotUsernameNISS.vue');
const ForgotUsernameCard = () => import('@/pages/ForgotUsername/ForgotUsernameCard.vue');

const CustomerOnboarding = () => import('@/pages/CustomerOnboarding/CustomerOnboarding.vue');
const CustomerOnboardingHome = () => import('@/pages/CustomerOnboarding/CustomerOnboardingHome.vue');
const OnboardingEmailEntry = () => import('@/pages/Onboarding/OnboardingEmailEntry.vue');
const CustomerOnboardingMfa = () => import('@/pages/CustomerOnboarding/CustomerOnboardingMfa.vue');
const CustomerOnboardingSuccess = () => import('@/pages/CustomerOnboarding/CustomerOnboardingSuccess.vue');

const NotificationPreferences = () => import('@/pages/NotificationPreferences/NotificationPreferences.vue');
const NotificationPreferencesOverview = () => import('@/pages/NotificationPreferences/Overview.vue');
const Notifications = () => import('@/pages/Notifications/Notifications.vue');
const NotificationsOverview = () => import('@/pages/Notifications/Overview.vue');

// Steps that exist only after the invitation code/card has been consumed (so an onboarding token is held).
// Used to persist progress and to resume the flow after a refresh or a return visit.
const onboardingResumeSteps = [
	'onboarding-mfa-selection',
	'onboarding.email-verification',
	'onboarding.phone-verification',
	'onboarding-totp-verification',
	'onboarding-mfa',
	'onboarding.totp-recovery-keys',
	'onboarding.create',
	'onboarding.terms-conditions',
	'onboarding.password',
];

// Steps after the password has been set (the account now exists with a real session). They're mandatory, so
// while onboarding is pending the user is kept on the saved step from anywhere until the flow completes.
const onboardingPostPasswordSteps = ['onboarding.confirmation'];

// Routes a logged-out user with a pending onboarding may still reach (to authenticate and come back).
const onboardingAuthEscapeRoutes = ['login', 'login.email', 'login.phone', 'maintenance'];

// All onboarding steps whose progress is persisted while the flow is pending.
const onboardingStepsToPersist = [...onboardingResumeSteps, ...onboardingPostPasswordSteps];

// Set while the global guard is restoring an interrupted onboarding session, so the per-step
// allow-list guards let the resume navigation through (the usual `from` chain is absent on a cold load).
let resumingOnboarding = false;

const allowNavigationFrom = (allowedRoutes: string[], fallback: string = 'onboarding') => {
	return (_to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) => {
		if (resumingOnboarding || allowedRoutes.includes(from.name as string)) {
			next();
		} else {
			next({ name: fallback });
		}
	};
};

const customerOnboardingInProgress = [
	'customer-onboarding-home',
	'customer-onboarding-mfa',
	'customer-onboarding-password',
	'customer-onboarding-success',
	'onboarding.magic-link',
	'onboarding.totp-recovery-keys',
	'onboarding.terms-conditions',
	'onboarding.create',
	'onboarding.confirmation',
];

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: [
		{
			path: '/logout',
			name: 'logout',
			component: Logout,
		},
		{
			path: '/sso/microsoft/login/callback',
			name: 'microsoft-sso',
			component: MicrosoftCallback,
		},
		{
			path: '',
			component: LoggedOutLayout,
			children: [
				{
					path: '',
					name: 'home',
					component: OnBoardingHome,
				},
				{
					path: '/maintenance',
					name: 'maintenance',
					component: Maintenance,
				},
				{
					path: '/customer-onboarding',
					component: CustomerOnboarding,
					children: [
						{
							path: '',
							name: 'customer-onboarding-home',
							component: CustomerOnboardingHome,
						},
						{
							path: 'password',
							name: 'customer-onboarding-password',
							component: OnboardingPasswordSetup,
							beforeEnter: allowNavigationFrom(
								['customer-onboarding-mfa', 'customer-onboarding-email', 'onboarding.totp-recovery-keys'],
								'customer-onboarding-home',
							),
						},
						{
							path: 'success',
							name: 'customer-onboarding-success',
							component: CustomerOnboardingSuccess,
							beforeEnter: allowNavigationFrom(
								['customer-onboarding-password', 'login', 'mfa', 'microsoft-sso'],
								'customer-onboarding-home',
							),
						},
					],
				},
				{
					path: '/login',
					name: 'login',
					component: Login,
					children: [
						{
							path: 'email',
							name: 'login.email',
							component: LoginEmail,
						},
						{
							path: 'phone',
							name: 'login.phone',
							component: LoginPhone,
						},
					],
				},
				{
					path: '/onboarding',
					component: Onboarding,
					children: [
						{
							path: '',
							name: 'onboarding',
							component: OnBoardingHome,
						},
						{
							path: 'card',
							name: 'onboarding.card',
							component: OnboardingCard,
							beforeEnter: allowNavigationFrom(['onboarding', 'home']),
							meta: {
								breadcrumb: [
									{ label: 'breadcrumb.onboarding.account_creation', to: '/onboarding' },
									{ label: 'breadcrumb.onboarding.card' },
								],
							},
						},
						{
							path: 'code',
							name: 'onboarding.code',
							component: OnboardingCode,
							beforeEnter: allowNavigationFrom(['onboarding', 'home']),
							meta: {
								breadcrumb: [
									{ label: 'breadcrumb.onboarding.account_creation', to: '/onboarding' },
									{ label: 'breadcrumb.onboarding.code' },
								],
							},
						},
						{
							path: 'terms-conditions',
							name: 'onboarding.terms-conditions',
							component: OnboardingTerms,
							beforeEnter: allowNavigationFrom(['onboarding.create']),
							meta: {
								breadcrumb: [
									{ label: 'breadcrumb.onboarding.account_creation', to: '/onboarding' },
									{ label: 'breadcrumb.onboarding.terms' },
								],
							},
						},
						{
							path: 'create',
							name: 'onboarding.create',
							component: OnboardingCreate,
							beforeEnter: allowNavigationFrom(['onboarding.code', 'onboarding.card', 'onboarding.magic-link']),
							meta: {
								breadcrumb: [
									{ label: 'breadcrumb.onboarding.account_creation', to: '/onboarding' },
									{ label: 'breadcrumb.onboarding.personal_info' },
								],
							},
						},
						{
							path: 'confirmation',
							name: 'onboarding.confirmation',
							component: OnboardingConfirmation,
							beforeEnter: allowNavigationFrom(['onboarding.password', 'onboarding.create']),
						},
						{
							path: 'password',
							name: 'onboarding.password',
							component: OnboardingPasswordSetup,
							beforeEnter: allowNavigationFrom(['onboarding-mfa', 'onboarding.phone-verification', 'onboarding.totp-recovery-keys']),
						},
						{
							path: 'magic-link',
							name: 'onboarding.magic-link',
							component: OnboardingMagicLink,
							meta: {
								breadcrumb: [
									{ label: 'breadcrumb.onboarding.account_creation', to: '/onboarding' },
									{ label: 'breadcrumb.onboarding.magic_link' },
								],
							},
						},
					],
				},
				{
					path: '/need-help',
					name: 'need-help',
					component: NeedHelp,
				},
				{
					path: '/forgot-password',
					name: 'forgot-password',
					component: ForgotPassword,
					children: [
						{
							path: 'email',
							name: 'forgot-password.email',
							component: ForgotPasswordEmail,
						},
						{
							path: 'phone',
							name: 'forgot-password.phone',
							component: ForgotPasswordPhone,
						},
					],
				},
				{
					path: '/reset-password',
					name: 'reset-password',
					component: ResetPassword,
				},
				{
					path: '/reset-password-confirmed',
					name: 'reset-password-confirmed',
					component: ResetPasswordConfirmed,
				},
				{
					path: '/weak-password',
					name: 'weak-password',
					component: WeakPassword,
				},
				{
					path: '/forgot-username',
					name: 'forgot-username',
					component: ForgotUsername,
					children: [
						{
							path: 'card',
							name: 'forgot-username.card',
							component: ForgotUsernameCard,
						},
						{
							path: 'niss',
							name: 'forgot-username.niss',
							component: ForgotUsernameNISS,
						},
					],
				},
				{
					path: '/forgot-username-response',
					name: 'forgot-username-response',
					component: ForgotUsernameResponse,
					beforeEnter: allowNavigationFrom(['forgot-username.card', 'forgot-username.niss', 'auth-itsme'], 'home'),
				},
				{
					path: '/auth/itsme',
					name: 'auth-itsme',
					component: ItsmeAccountRecovery,
				},
				{
					path: '/account-locked',
					name: 'account-locked',
					component: AccountLocked,
				},
				{
					path: '/partner/request-access',
					name: 'partner-request-access',
					component: PartnerRequestAccess,
				},
				{
					path: '/partner/no-account',
					name: 'partner-no-account',
					component: PartnerNoAccount,
				},
				{
					path: '/partner/link-existing',
					name: 'partner-link-existing-account',
					component: PartnerLinkExistingAccount,
				},
				{
					path: '/partner/link-success',
					name: 'partner-link-success',
					component: PartnerLinkSuccess,
				},
				{
					path: '/approve',
					name: 'approve',
					component: Approve,
				},
				{
					path: '/duplicated',
					name: 'duplicated',
					component: OnboardingAccountFound,
					meta: {
						breadcrumb: [
							{ label: 'breadcrumb.onboarding.account_creation', to: '/onboarding' },
							{ label: 'breadcrumb.onboarding.account_found' },
						],
					},
				},
				{
					path: '/fuzzion-success',
					name: 'fuzzion-success',
					component: OnboardingAccountMerged,
				},
				{
					path: '/setup-mfa/success',
					name: 'setup-mfa-success',
					component: SetupMfaSuccess,
					beforeEnter: allowNavigationFrom(['setup-mfa-confirmation'], 'home'),
				},
			],
		},
		{
			path: '',
			component: MfaLayout,
			children: [
				{
					path: '/mfa',
					name: 'mfa',
					component: MfaValidation,
				},
				{
					path: '/forgot-password-sent',
					name: 'forgot-password-sent',
					component: ForgotPasswordSent,
					beforeEnter: allowNavigationFrom(['forgot-password.email', 'forgot-password.phone'], 'home'),
				},
				{
					path: '/mfa/totp-recovery',
					name: 'mfa-totp-recovery',
					component: MfaTotpRecoveryValidation,
				},
				{
					path: '/mfa/methods',
					name: 'mfa-methods',
					component: MfaMethodSelection,
				},
				{
					path: '/mfa/too-many-attempts',
					name: 'mfa-too-many-attempts',
					component: MfaTooManyAttempts,
					beforeEnter: allowNavigationFrom(['mfa', 'setup-mfa-confirmation'], 'mfa'),
				},
				{
					path: '/setup-mfa',
					name: 'setup-mfa',
					component: ForceMfa,
				},
				{
					path: '/setup-mfa/selection',
					name: 'setup-mfa-selection',
					component: SetupMfaSelectionCustomer,
				},
				{
					path: '/setup-mfa/phone',
					name: 'setup-mfa-phone',
					component: SetupMfaPhone,
				},
				{
					path: '/setup-mfa/email',
					name: 'setup-mfa-email',
					component: SetupMfaEmail,
				},
				{
					path: '/setup-mfa/totp',
					name: 'setup-mfa-totp',
					component: SetupMfaTotp,
				},
				{
					path: '/setup-mfa/confirmation',
					name: 'setup-mfa-confirmation',
					component: SetupMfaConfirmation,
					beforeEnter: allowNavigationFrom(['setup-mfa-totp', 'setup-mfa-email', 'setup-mfa-phone'], 'home'),
				},
				{
					path: '/setup-totp/success',
					name: 'setup-totp-success',
					component: SetupTotpSuccess,
					beforeEnter: allowNavigationFrom(['setup-mfa-confirmation', 'mfa-totp-recovery'], 'home'),
				},
				{
					path: '/totp/redirect',
					name: 'totp-success-redirect',
					component: SetupTotpSuccessRedirect,
					beforeEnter: allowNavigationFrom(['setup-totp-success'], 'home'),
				},
				{
					path: '/onboarding-mfa',
					name: 'onboarding-mfa',
					component: OnboardingMFA,
					beforeEnter: allowNavigationFrom([
						'onboarding.email-verification',
						'onboarding.phone-verification',
						'onboarding-totp-verification',
					]),
				},
				{
					path: '/onboarding-mfa-selection',
					name: 'onboarding-mfa-selection',
					component: OnboardingMfaMethodChoice,
					beforeEnter: allowNavigationFrom([
						'onboarding-mfa',
						'onboarding.email-verification',
						'onboarding.phone-verification',
						'onboarding-totp-verification',
						'onboarding.terms-conditions',
						'onboarding.create',
					]),
				},
				{
					path: '/onboarding-totp-verification',
					name: 'onboarding-totp-verification',
					component: OnboardingTotpVerification,
					beforeEnter: allowNavigationFrom(['onboarding-mfa-selection', 'onboarding-mfa']),
				},
				{
					path: '/onboarding/totp-recovery-keys',
					name: 'onboarding.totp-recovery-keys',
					component: OnboardingTotpRecoveryKeys,
					beforeEnter: allowNavigationFrom(['onboarding-mfa', 'customer-onboarding-mfa']),
				},
				{
					path: '/customer-onboarding/email',
					name: 'customer-onboarding-email',
					component: OnboardingEmailEntry,
					beforeEnter: allowNavigationFrom(['customer-onboarding-home'], 'customer-onboarding-home'),
				},
				{
					path: '/onboarding/email-verification',
					name: 'onboarding.email-verification',
					component: OnboardingEmailEntry,
					beforeEnter: allowNavigationFrom(['onboarding.terms-conditions', 'onboarding.create', 'onboarding.magic-link']),
				},
				{
					path: '/onboarding/phone-verification',
					name: 'onboarding.phone-verification',
					component: OnboardingMfaPhoneEntry,
					beforeEnter: allowNavigationFrom(['onboarding-mfa', 'onboarding-mfa-selection']),
				},
				{
					path: '/customer-onboarding/mfa',
					name: 'customer-onboarding-mfa',
					component: CustomerOnboardingMfa,
					beforeEnter: allowNavigationFrom(['customer-onboarding-email'], 'customer-onboarding-home'),
				},
			],
		},
		{
			path: '',
			component: LoggedInLayout,
			children: [
				{
					// Only reachable while authenticated (the beforeEach guard bounces guests to login),
					// so it renders the "Loading your access" loader inside the logged-in chrome.
					path: '/partner',
					name: 'partner',
					component: Partner,
					props: true,
				},
				{
					path: '/user/dashboard',
					name: 'user-dashboard',
					component: Dashboard,
				},
				{
					path: '/user/access',
					name: 'user-access',
					component: Access,
				},
				{
					path: '/profile',
					name: 'profile',
					component: ProfileOverview,
				},
				{
					path: '/profile/edit-info',
					name: 'profile.edit-info',
					component: EditInfo,
					beforeEnter: allowNavigationFrom(['profile']),
				},
				{
					path: '/profile/edit-credentials',
					name: 'profile.edit-credentials',
					component: EditCredentials,
				},
				{
					path: '/security',
					name: 'security',
					component: SecurityOverview,
				},
				{
					path: '/security/mfa-edit',
					name: 'security.mfa-edit',
					component: MfaEdit,
				},
				{
					path: '/security/edit-recovery',
					name: 'security.edit-recovery',
					component: EditRecovery,
				},
				{
					path: '/security/edit-password',
					name: 'security.edit-password',
					component: PasswordEdit,
				},
				{
					path: '/security/history',
					name: 'security.history',
					component: History,
				},
				{
					path: '/notification-preferences',
					component: NotificationPreferences,
					children: [
						{
							path: '',
							name: 'notification-preferences',
							component: NotificationPreferencesOverview,
						},
					],
				},
				{
					path: '/notifications',
					component: Notifications,
					children: [
						{
							path: '',
							name: 'notifications',
							component: NotificationsOverview,
						},
					],
				},
			],
		},
		{
			path: '/:pathMatch(.*)*',
			name: 'NotFound',
			redirect: { name: 'home' },
		},
	],
});
router.beforeEach(async (to, from) => {
	const authStore = useAuthStore();
	const userStore = useUserStore();

	const isMaintenanceMode: boolean = import.meta.env.VITE_MAINTENANCE !== 'false';

	if (isMaintenanceMode && to.name === 'maintenance') {
		return;
	}
	if (isMaintenanceMode && !useStorage('admin', false).value) {
		return { name: 'maintenance' };
	}

	// Keep the user inside the mandatory post-password onboarding steps until the flow completes. After the
	// password step the account already exists with a real session, so a refresh or reconnect would otherwise
	// drop them on the dashboard with the (mandatory) terms/personal-info steps skipped. Completion can't be
	// inferred from profile data — some fields are pre-filled by the employer — so we rely on the persisted
	// pending marker + step. Runs before the home→dashboard redirect below so it takes precedence.
	if (useStorage('onboarding_pending', '').value) {
		const savedStep = useStorage('onboarding_step', '').value;
		if (onboardingPostPasswordSteps.includes(savedStep)) {
			if (authStore.isLogged) {
				const onboardingStore = useOnboardingStore();
				if (!onboardingStore.user.id) {
					try {
						// Repopulate the store so the personal-info form pre-fills again after a cold load.
						await onboardingStore.getUserInfos();
					} catch {
						// A 401 here triggers Api's own logout + reload; any other error is non-fatal, the step refetches.
					}
				}
				// Allow moving freely between the mandatory steps (forward progress and refresh); only redirect
				// back to the saved step when the user tries to leave the flow (e.g. straight to the dashboard).
				resumingOnboarding = true;
				if (!onboardingPostPasswordSteps.includes(to.name?.toString() || '')) {
					return { name: savedStep };
				}
			} else if (!onboardingAuthEscapeRoutes.includes(to.name?.toString() || '')) {
				authStore.setTargetUrl(to);
				return { name: 'login.email' };
			}
		}
	}

	// A pending weak-password change is mandatory: a logged-in user can't reach the app until it's done.
	// Allow the auth/MFA flow routes (the password step can follow a forced/existing-MFA challenge) plus the
	// login/logout escapes; force every other destination back to the weak-password page so it can't be skipped.
	if (sessionStorage.getItem('weak_password_pending') && authStore.isLogged) {
		const target = to.name?.toString() || '';
		const allowedDuringWeak =
			target === 'weak-password' ||
			target === 'reset-password-confirmed' ||
			target === 'logout' ||
			target.startsWith('login') ||
			target.includes('mfa');
		if (!allowedDuringWeak) {
			return { name: 'weak-password', query: { verified: 'true' } };
		}
	}

	if ((to.name === 'home' || to.path === '/') && authStore.isLogged) {
		return { path: '/user/dashboard' };
	}

	if (window.location.hash) {
		fragmentParser(window.location.hash.slice(1));
		window.location.href = window.location.href.split('#')[0];
	}

	// Resume an interrupted onboarding. A refresh or return visit mid-flow lands on a step route with the
	// onboarding token still in localStorage but no in-memory session. Restore it and send the user back to
	// their saved step instead of dead-ending on the (already consumed) invitation code. If the token has
	// expired/been revoked, getUserInfos throws and we fall back to the onboarding start with a notice.
	if (!authStore.token && onboardingResumeSteps.includes(to.name?.toString() || '')) {
		const onboardingStore = useOnboardingStore();
		const onboardingToken = useStorage('onboarding_token', '').value;
		const savedStep = useStorage('onboarding_step', '').value;
		if (onboardingToken && savedStep) {
			try {
				await onboardingStore.restoreOnboardingToken();
				resumingOnboarding = true;
				// The MFA sub-steps depend on transient state (codes, payloads, TOTP secret, recovery keys) that
				// a cold load can't rebuild, so resume to the nearest step that reconstructs itself from the
				// restored user: the personal-info, terms or password step if MFA was already done, otherwise the
				// MFA method choice.
				const target = ['onboarding.create', 'onboarding.terms-conditions', 'onboarding.password'].includes(savedStep)
					? savedStep
					: 'onboarding-mfa-selection';
				if (to.name !== target) {
					return { name: target };
				}
			} catch {
				await onboardingStore.exitOnboarding();
				onboardingStore.completeOnboarding();
				return { name: 'onboarding', query: { expired: '1' } };
			}
		}
	}

	authStore.token !== '' ? (client.axios().defaults.headers.common['Authorization'] = 'Bearer ' + authStore.token) : null;

	const needGuest: boolean = [
		'home',
		'login.email',
		'login.phone',
		'need-help',
		'forgot-password.email',
		'forgot-password.phone',
		'forgot-password-sent',
		'reset-password',
		'reset-password-confirmed',
		'auth-itsme',
		'account-locked',
		'forgot-username.card',
		'forgot-username.niss',
		'forgot-username-response',
		'weak-password',
		'onboarding',
		'onboarding.card',
		'onboarding.code',
		'onboarding.create',
		'onboarding.password',
		'onboarding.phone-verification',
		'onboarding.email-verification',
		'onboarding-mfa-selection',
		'onboarding-totp-verification',
		'onboarding.terms-conditions',
		'onboarding-mfa',
		'onboarding.totp-recovery-keys',
		'onboarding.magic-link',
		'customer-onboarding-home',
		'customer-onboarding-email',
		'customer-onboarding-mfa',
		'customer-onboarding-password',
		'customer-onboarding-success',
		'partner-link-existing-account',
		'microsoft-sso',
		'partner-no-account',
	].includes(to?.name?.toString() || '');

	const monizzeOnly: boolean = ['forgot-username.card', 'forgot-username.niss', 'auth-itsme', 'account-locked'].includes(
		to?.name?.toString() || '',
	);

	const isFuzzion: boolean = ['duplicated', 'fuzzion-success', 'mfa'].includes(to?.name?.toString() || '');

	const isUnprotected: boolean = ['mfa', 'mfa-methods'].includes(to?.name?.toString() || '');
	const urlParams = new URLSearchParams(authStore.targetUrl.split('?')[1]);
	const oauthClients: Array<string> = import.meta.env.VITE_CLIENT_ID.split(',');
	const clientId = urlParams.get('client_id');
	const isMFAProtected: boolean = [
		'security.mfa-edit',
		'security.edit-recovery',
		'security.edit-password',
		'profile.edit-credentials',
		'partner.link-success',
	].includes(to?.name?.toString() || '');

	if (to?.name?.toString() === 'login') {
		return { name: 'login.email' };
	}

	if (authStore.isLogged) {
		try {
			useStorage('account', '{}').value = JSON.stringify(useUserStore().user);
		} catch {
			console.info('No local storage available: you will need to log again after refreshing the page');
		}
	}

	if (isMFAProtected) {
		let hasSudo = false;
		try {
			hasSudo = await userStore.isMfa(to.name?.toString() === 'security.edit-password');
		} catch {
			hasSudo = false;
		}
		if (!hasSudo) {
			return { name: 'mfa-methods', query: { single: 'true', redirect: to.name?.toString() } };
		}
	}

	if (!isUnprotected) {
		if (
			sessionStorage.getItem('mfa_forced') &&
			(!clientId || oauthClients.includes(clientId)) &&
			!from.name?.toString().includes('setup-mfa') &&
			!to.name?.toString().includes('setup-mfa') &&
			!customerOnboardingInProgress.includes(to?.name?.toString() || '') &&
			!isFuzzion &&
			!needGuest
		) {
			return { name: 'setup-mfa' };
		}
		if (
			sessionStorage.getItem('mfa_forced') &&
			!sessionStorage.getItem('is_recovery') &&
			!sessionStorage.getItem('weak_password_pending') &&
			from.name?.toString().includes('setup-mfa') &&
			from.name?.toString() !== 'setup-mfa-success' &&
			!to.name?.toString().includes('setup-mfa') &&
			!isFuzzion
		) {
			authStore.logout();
			return { name: 'login.email' };
		}
		if (
			authStore.isLogged &&
			!sessionStorage.getItem('is_recovery') &&
			!sessionStorage.getItem('weak_password_pending') &&
			(needGuest || (authStore.isLux && monizzeOnly)) &&
			!customerOnboardingInProgress.includes(to?.name?.toString() || '')
		) {
			return { name: 'home' };
		}

		if (!authStore.isLogged && !authStore.isDuplicatedLogged && !isFuzzion && (!needGuest || (authStore.isLux && monizzeOnly))) {
			authStore.setTargetUrl(to);
			return { name: 'home' };
		}
	}
});

let previousPageUrl: string | null = null;

router.afterEach((to) => {
	// Record the current onboarding step (while the flow is pending) so a later refresh/return/reconnect can
	// resume here. Gated on the pending marker so a completed user revisiting a step URL can't re-arm the lock.
	if (useStorage('onboarding_pending', '').value && onboardingStepsToPersist.includes(to.name?.toString() || '')) {
		useStorage('onboarding_step', '').value = to.name?.toString() || '';
	}
	resumingOnboarding = false;

	const pageReferrer = previousPageUrl || document.referrer || '';

	nextTick(() => {
		pushEvent({ event: AnalyticsEvents.VIRTUAL_PAGE_VIEW, pageReferrer });
	});

	previousPageUrl = window.location.href;
});

export default router;
